import EventIcon from '@mui/icons-material/Event';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { Box, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';

import { useFeatureFlagSplit } from '@verantos-dev/core-utils';
import { LoadingSpinner } from '@verantos-dev/shared-components';

const StatItem = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
      <Box sx={{ display: 'flex', alignItems: 'center', color: theme.palette.text.secondary }}>
        {icon}
      </Box>
      <Typography
        variant="subtitle2"
        sx={{ color: theme.palette.text.secondary, fontWeight: '400' }}
      >
        {label}: {value ? dayjs(value).format('MMM D, YYYY') : '-'}
      </Typography>
    </Box>
  );
};

interface ProjectStatsProps {
  createdOn: string;
  startedOn: string;
  completedOn: string;
  isLoading: boolean;
}

export const ProjectStats = ({
  createdOn,
  startedOn,
  completedOn,
  isLoading,
}: ProjectStatsProps) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  // TODO: cleanup with cp_annotation_show_dashboard_loading_spinner ff
  // Shared across feature-annotation-project-admin and feature-supervisor-projects, so the
  // loading spinner is scoped locally here rather than lifted to either screen.
  const showLoadingSpinner = useFeatureFlagSplit(
    'cp_annotation_show_dashboard_loading_spinner'
  ) as boolean;

  if (showLoadingSpinner && isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80px' }}>
        <LoadingSpinner id="projectStatsLoading" />
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(6), p: theme.spacing(2) }}>
      <StatItem
        icon={<EventIcon sx={{ width: 16, height: 16 }} />}
        label={formatMessage({ id: 'adminProjects.projectDetails.stats.createdOn' })}
        value={createdOn}
      />
      <StatItem
        icon={<PlayArrowIcon sx={{ width: 16, height: 16 }} />}
        label={formatMessage({ id: 'adminProjects.projectDetails.stats.startedOn' })}
        value={startedOn}
      />
      <StatItem
        icon={<TaskAltIcon sx={{ width: 16, height: 16 }} />}
        label={formatMessage({ id: 'adminProjects.projectDetails.stats.completedOn' })}
        value={completedOn}
      />
    </Box>
  );
};
